import React, { useEffect, useMemo, useState } from 'react';
import { translations } from '../translations';

const STORAGE_KEY = 'cm_cookie_consent_v1';

function readStoredConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredConsent(payload) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore localStorage failures
  }
}

function CookieConsentBanner({
  language = 'ro',
  onOpenPrivacy,
  onOpenCookies,
  onOpenTerms
}) {
  const t = translations[language];
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const saved = readStoredConsent();
    if (!saved) {
      setVisible(true);
      return;
    }
    setVisible(false);
  }, []);

  const payloadBase = useMemo(
    () => ({
      version: 1,
      necessary: true,
      updatedAt: new Date().toISOString()
    }),
    []
  );

  const saveAndClose = (payload) => {
    writeStoredConsent(payload);
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => {
    saveAndClose({
      ...payloadBase,
      mode: 'accept_all',
      analytics: true,
      marketing: true
    });
  };

  const rejectOptional = () => {
    saveAndClose({
      ...payloadBase,
      mode: 'reject_optional',
      analytics: false,
      marketing: false
    });
  };

  const savePreferences = () => {
    saveAndClose({
      ...payloadBase,
      mode: 'preferences',
      analytics: Boolean(analytics),
      marketing: Boolean(marketing)
    });
  };

  if (!visible) return null;

  return (
    <div className="cookie-banner-wrap" role="dialog" aria-live="polite" aria-label={t.cookieBannerTitle}>
      <div className="cookie-banner-card">
        <h3>{t.cookieBannerTitle}</h3>
        <p>{t.cookieBannerText}</p>
        <div className="cookie-banner-links">
          <button type="button" className="cookie-inline-link" onClick={onOpenCookies}>
            {t.footerCookies}
          </button>
          <button type="button" className="cookie-inline-link" onClick={onOpenPrivacy}>
            {t.footerPrivacy}
          </button>
          <button type="button" className="cookie-inline-link" onClick={onOpenTerms}>
            {t.footerTerms}
          </button>
        </div>

        {showPrefs && (
          <div className="cookie-pref-panel">
            <label className="cookie-pref-row">
              <input type="checkbox" checked disabled />
              <span>{t.cookieNecessary}</span>
            </label>
            <label className="cookie-pref-row">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
              />
              <span>{t.cookieAnalytics}</span>
            </label>
            <label className="cookie-pref-row">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
              />
              <span>{t.cookieMarketing}</span>
            </label>
          </div>
        )}

        <div className="cookie-banner-actions">
          <button type="button" className="cookie-btn cookie-btn-light" onClick={rejectOptional}>
            {t.cookieReject}
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn-mid"
            onClick={showPrefs ? savePreferences : () => setShowPrefs(true)}
          >
            {showPrefs ? t.cookieSavePrefs : t.cookiePrefs}
          </button>
          <button type="button" className="cookie-btn cookie-btn-strong" onClick={acceptAll}>
            {t.cookieAccept}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsentBanner;
